import * as tus from 'tus-js-client'
import axios from '../utils/axios'
import axiosRaw from 'axios'
import { API_BASE_URL } from '../constants'
import { useUploadTrackerStore } from '../zustand/uploadStore'

const DEFAULT_CONTENT_TYPE = 'application/octet-stream'
const S3_CONFIG_TTL_MS = 5 * 60 * 1000
const obsAxios = axiosRaw.create({
  withCredentials: false,
})

// Create a unique key for files to identify them for resumption
const generateFileKey = (file) => {
  // Create a unique identifier based on file name, size, and last modified date
  const fileIdentifier = `${file.name}-${file.size}-${file.lastModified}`
  // Handle unicode characters by encoding to UTF-8 first
  return btoa(unescape(encodeURIComponent(fileIdentifier))).replace(/[+/=]/g, '') // base64 encode and make URL-safe
}

const determineFileCategory = (file, providedCategory) => {
  if (providedCategory) return providedCategory.toLowerCase()
  const filename = (file?.name || '').toLowerCase()

  if (/\.(fastq|fq)(\.gz)?$/.test(filename)) return 'fastq'
  if (/\.vcf(\.gz)?$/.test(filename)) return 'vcf'
  if (/\.bam$/.test(filename)) return 'ont_bam'
  if (/\.bed$/.test(filename)) return 'bed'
  if (/\.(txt|csv)$/.test(filename)) return 'gene_panel'
  return 'unknown'
}

const cloneS3Session = (session) => {
  if (!session) return null
  return {
    ...session,
    parts: Array.isArray(session.parts) ? session.parts.map((part) => ({ ...part })) : [],
  }
}

// Class to manage upload instances and states
class UploadService {
  constructor() {
    this.uploads = {} // Store all active upload instances
    this.initialized = false
    this.s3Config = null
    this.s3ConfigFetchedAt = null

    // Auto-initialize on creation
    this.init()
  }

  // Initialize the service and connect it to the store
  init() {
    if (this.initialized) return

    // Get store actions
    this.store = {
      addUpload: useUploadTrackerStore.getState().addUpload,
      updateUploadProgress: useUploadTrackerStore.getState().updateUploadProgress,
      completeUpload: useUploadTrackerStore.getState().completeUpload,
      failUpload: useUploadTrackerStore.getState().failUpload,
      cancelUpload: useUploadTrackerStore.getState().cancelUpload,
      interruptUpload: useUploadTrackerStore.getState().interruptUpload,
      resumeUpload: useUploadTrackerStore.getState().resumeUpload,
      getInterruptedUploads: useUploadTrackerStore.getState().getInterruptedUploads,
      pauseUpload: useUploadTrackerStore.getState().pauseUpload,
      resumePausedUpload: useUploadTrackerStore.getState().resumePausedUpload,
      clearInterruptedUploads: useUploadTrackerStore.getState().clearInterruptedUploads,
      updateUploadMetadata: useUploadTrackerStore.getState().updateUploadMetadata,
    }

    // Setup event listeners for page unload to track interrupted uploads
    this.setupPageUnloadHandlers()

    this.initialized = true

    // Check for interrupted uploads immediately
    this.checkForInterruptedUploads()
  }

  // Setup handlers for when the page is closed or refreshed
  setupPageUnloadHandlers() {
    window.addEventListener('beforeunload', () => {
      // Mark all active uploads as interrupted when the page is about to be unloaded
      Object.keys(this.uploads).forEach((uploadId) => {
        this.store.interruptUpload(uploadId)
      })
    })
  }

  // Check for any interrupted uploads that need to be resumed
  checkForInterruptedUploads() {
    const interruptedUploads = this.store.getInterruptedUploads()

    if (interruptedUploads.length > 0) {
      console.log('Found interrupted uploads:', interruptedUploads.length)
    }
  }

  // Check if a file has an interrupted upload
  getInterruptedUploadByFileKey(fileKey) {
    const interruptedUploads = this.store.getInterruptedUploads()
    return interruptedUploads.find((upload) => upload.fileKey === fileKey)
  }

  // Validate if a selected file matches an interrupted upload
  validateFileMatch(file, interruptedUpload) {
    if (!file || !interruptedUpload) return false

    // Check for this pattern: filename-size-lastModified
    const fileKey = generateFileKey(file)
    const originalKey = interruptedUpload.fileKey

    // If keys match exactly, it's the same file
    if (fileKey === originalKey) {
      return { isMatch: true }
    }

    // If not, extract file details to determine what's different
    const [origName, origSize] =
      interruptedUpload.filename && interruptedUpload.totalBytes
        ? [interruptedUpload.filename, interruptedUpload.totalBytes]
        : ['unknown', 0]

    const nameMatch = file.name === origName
    const sizeMatch = file.size === origSize

    return {
      isMatch: false,
      nameMatch: nameMatch,
      sizeMatch: sizeMatch,
      details: {
        original: {
          name: origName,
          size: origSize,
        },
        current: {
          name: file.name,
          size: file.size,
        },
      },
    }
  }

  // Start a new upload or resume an interrupted one
  async startUpload(file, options = {}) {
    this.init()

    const normalizedOptions = {
      ...options,
      metadata: {
        ...(options.metadata || {}),
      },
    }

    // Generate a unique key for this file
    const fileKey = generateFileKey(file)

    // Check if this file has an interrupted upload
    const interruptedUpload = this.getInterruptedUploadByFileKey(fileKey)

    // If attempting to resume (explicitly provided in options) but files don't match
    if (normalizedOptions.resuming && normalizedOptions.uploadToResume && !interruptedUpload) {
      // Validate if the file matches what we're trying to resume
      const validationResult = this.validateFileMatch(file, normalizedOptions.uploadToResume)

      if (!validationResult.isMatch) {
        // If file mismatch and user chose to proceed anyway, use the new file
        // but add a special flag to indicate this isn't a perfect match
        if (normalizedOptions.forceProceed) {
          // We'll continue but mark this as a force-resumed upload
          normalizedOptions.metadata = {
            ...normalizedOptions.metadata,
            forceResumed: true,
            originalFile: normalizedOptions.uploadToResume.filename,
          }
        } else {
          // Otherwise, notify about the mismatch and let the caller handle it
          if (normalizedOptions.onMismatch) {
            normalizedOptions.onMismatch(validationResult)
          }
          return null
        }
      }
    }

    const baseMetadata = interruptedUpload?.metadata || {}
    const metadata = {
      ...baseMetadata,
      ...normalizedOptions.metadata,
    }

    if (baseMetadata?.s3Session && !metadata.s3Session) {
      metadata.s3Session = baseMetadata.s3Session
    }

    const fileCategory = determineFileCategory(file, normalizedOptions.fileCategory || metadata.fileCategory)
    metadata.fileCategory = fileCategory
    metadata.file_type = fileCategory
    metadata.filetype = metadata.filetype || file.type || DEFAULT_CONTENT_TYPE
    metadata.originalFilename = metadata.originalFilename || file.name

    // Ensure normalizedOptions.metadata includes the merged metadata (important for resuming after page refresh)
    normalizedOptions.metadata = metadata

    // Create a unique tracking ID for this upload or use the existing one
    const uploadId = interruptedUpload
      ? interruptedUpload.id
      : this.store.addUpload({
          filename: file.name,
          totalBytes: file.size,
          fileKey,
          metadata,
        })

    // If this is a resumed upload, update the status
    if (interruptedUpload) {
      this.store.updateUploadMetadata(uploadId, metadata)
      this.store.resumeUpload(uploadId)
    }

    try {
      const useS3 = await this.shouldUseS3Upload({
        fileCategory,
        interruptedUpload,
      })

      if (useS3) {
        try {
          const initialized = await this.initializeS3Upload({
            uploadId,
            file,
            fileKey,
            metadata,
            options: normalizedOptions,
          })

          if (!initialized && metadata.s3CompletePath) {
            throw new Error('S3 upload did not start')
          } else if (!initialized) {
            this.initializeTusUpload({ uploadId, file, fileKey, metadata, options: normalizedOptions })
          }
        } catch (error) {
          // A resumed session, or an upload that completes on its own route
          // (s3CompletePath), never falls back to TUS.
          if (interruptedUpload?.metadata?.s3Session || metadata.s3CompletePath) {
            throw error
          }
          console.warn('S3 initialization failed, falling back to TUS upload:', error)
          this.initializeTusUpload({ uploadId, file, fileKey, metadata, options: normalizedOptions })
        }
      } else if (metadata.s3CompletePath) {
        throw new Error('S3 uploads are not enabled')
      } else {
        this.initializeTusUpload({ uploadId, file, fileKey, metadata, options: normalizedOptions })
      }

      return uploadId
    } catch (error) {
      console.error('Upload failed to start:', error)
      this.store.failUpload(uploadId, error.message || 'Upload failed')
      if (normalizedOptions.onError) normalizedOptions.onError(error)
      delete this.uploads[uploadId]
      return null
    }
  }

  // Pause an active upload
  pauseUpload(uploadId) {
    if (!this.uploads[uploadId]) return false
    const tracked = this.uploads[uploadId]

    if (tracked.strategy === 's3') {
      try {
        tracked.pauseRequested = true
        tracked.isPaused = true
        if (tracked.currentAbortController) {
          tracked.currentAbortController.abort()
        }
        this.store.pauseUpload(uploadId)
        return true
      } catch (error) {
        console.error('Error pausing S3 upload:', error)
        return false
      }
    }

    try {
      // Abort without terminating - this pauses the upload
      tracked.instance.abort()

      // Update the store
      this.store.pauseUpload(uploadId)

      return true
    } catch (error) {
      console.error('Error pausing upload:', error)
      return false
    }
  }

  // Resume a paused upload
  resumePausedUpload(uploadId) {
    if (!this.uploads[uploadId]) return false
    const tracked = this.uploads[uploadId]

    if (tracked.strategy === 's3') {
      try {
        tracked.pauseRequested = false
        tracked.isPaused = false
        tracked.startTime = Date.now()
        tracked.startedBytesUploaded = this.calculateUploadedBytes(tracked.session, tracked.file.size)
        this.store.resumePausedUpload(uploadId)
        this.processS3Upload(uploadId)
        return true
      } catch (error) {
        console.error('Error resuming paused S3 upload:', error)
        return false
      }
    }

    try {
      // Start the upload again
      tracked.instance.start()

      // Update the store
      this.store.resumePausedUpload(uploadId)

      return true
    } catch (error) {
      console.error('Error resuming paused upload:', error)
      return false
    }
  }

  // Cancel a specific upload
  cancelUpload(uploadId) {
    if (!this.uploads[uploadId]) return false

    const tracked = this.uploads[uploadId]

    if (tracked.strategy === 's3') {
      tracked.cancelRequested = true
      if (tracked.currentAbortController) {
        tracked.currentAbortController.abort()
      }
      this.abortS3Upload(tracked.session)
      this.store.cancelUpload(uploadId)
      this.clearS3SessionMetadata(uploadId)
      delete this.uploads[uploadId]
      return true
    }

    try {
      tracked.instance.abort(true)
      this.store.cancelUpload(uploadId)
      delete this.uploads[uploadId]
      return true
    } catch (error) {
      console.error('Error cancelling upload:', error)
      return false
    }
  }

  // Cancel all active uploads
  cancelAllUploads() {
    Object.keys(this.uploads).forEach(this.cancelUpload.bind(this))
  }

  // Get all active upload instances
  getActiveUploads() {
    return this.uploads
  }

  // Clear only interrupted uploads
  clearInterruptedUploads() {
    this.store.clearInterruptedUploads()
  }

  getFileContentType(file, metadata = {}) {
    return metadata.filetype || file.type || DEFAULT_CONTENT_TYPE
  }

  async shouldUseS3Upload({ fileCategory, interruptedUpload }) {
    if (interruptedUpload?.metadata?.s3Session) {
      return true
    }

    try {
      const config = await this.getS3Config()
      if (config?.is_s3_enabled) {
        return true
      }

      return false
    } catch (error) {
      console.warn('Failed to load S3 config, falling back to TUS:', error)
      return false
    }
  }

  async getS3Config(force = false) {
    if (!force && this.s3Config && Date.now() - this.s3ConfigFetchedAt < S3_CONFIG_TTL_MS) {
      return this.s3Config
    }

    const { data } = await axios.get(`${API_BASE_URL}/upload/s3/config/`)
    this.s3Config = data
    this.s3ConfigFetchedAt = Date.now()
    return data
  }

  initializeTusUpload({ uploadId, file, fileKey, metadata, options }) {
    // Setup tracking variables
    let startTime = Date.now()
    let startedBytesUploaded = null

    const upload = new tus.Upload(file, {
      endpoint: `${API_BASE_URL}/upload/`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      chunkSize: process.env.REACT_APP_UPLOAD_CHUNK_SIZE_IN_MB
        ? 1024 * 1024 * parseInt(process.env.REACT_APP_UPLOAD_CHUNK_SIZE_IN_MB)
        : 1024 * 1024 * 10, // Default to 10MB if not set
      parallelUploads: process.env.REACT_APP_UPLOAD_PARALLEL_UPLOADS
        ? parseInt(process.env.REACT_APP_UPLOAD_PARALLEL_UPLOADS)
        : 1,
      metadata: {
        filename: file.name,
        filetype: metadata.filetype || file.type || DEFAULT_CONTENT_TYPE,
        fileKey,
        ...metadata,
      },
      headers: {
        Authorization: axios.defaults.headers.common.Authorization,
      },
      onError: (error) => {
        console.log('Upload failed: ' + error)
        this.store.failUpload(uploadId, error.message || 'Upload failed')
        if (options.onError) options.onError(error)
        delete this.uploads[uploadId]
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (startedBytesUploaded === null) {
          startedBytesUploaded = bytesUploaded
        }

        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2)
        const elapsedTime = Date.now() - startTime
        const uploadSpeed = (bytesUploaded - startedBytesUploaded) / (elapsedTime / 1000) // bytes per second
        const remainingBytes = bytesTotal - bytesUploaded

        let timeRemaining = 0
        if (uploadSpeed > 0) {
          timeRemaining = Math.round(remainingBytes / uploadSpeed) // seconds
        }

        // Update tracking store
        this.store.updateUploadProgress(uploadId, percentage, timeRemaining)

        // Call callback if provided
        if (options.onProgress) {
          options.onProgress(percentage, timeRemaining)
        }
      },
      onSuccess: () => {
        this.store.completeUpload(uploadId)
        if (options.onSuccess) options.onSuccess()
        delete this.uploads[uploadId]
      },
    })

    // Store the upload instance for potential later cancellation
    this.uploads[uploadId] = {
      strategy: 'tus',
      instance: upload,
      file,
    }

    upload.findPreviousUploads().then((previousUploads) => {
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0])
      }
      upload.start()
    })
  }

  async initializeS3Upload({ uploadId, file, metadata, options }) {
    const existingSession = metadata.s3Session ? cloneS3Session(metadata.s3Session) : null
    const session = existingSession || (await this.createS3UploadSession({ file, metadata }))

    if (!session) {
      return false
    }

    const totalParts = this.getPartCount(file.size, session.part_size_bytes)
    if (session.max_parts && totalParts > session.max_parts) {
      throw new Error('Selected file exceeds the maximum number of parts allowed for multipart uploads')
    }

    this.persistS3SessionMetadata(uploadId, session)

    const uploadedBytes = this.calculateUploadedBytes(session, file.size)

    this.uploads[uploadId] = {
      strategy: 's3',
      file,
      options,
      session,
      pauseRequested: false,
      cancelRequested: false,
      isPaused: false,
      startTime: Date.now(),
      startedBytesUploaded: uploadedBytes,
      processingPromise: null,
      currentAbortController: null,
    }

    this.processS3Upload(uploadId)
    return true
  }

  async createS3UploadSession({ file, metadata }) {
    const payload = {
      filename: metadata.originalFilename || file.name,
      file_type: metadata.fileCategory || determineFileCategory(file),
      file_size: file.size,
      content_type: this.getFileContentType(file, metadata),
    }

    const { data } = await axios.post(`${API_BASE_URL}/upload/s3/initiate/`, payload)

    return {
      ...data,
      file_type: data.file_type || payload.file_type,
      original_filename: data.original_filename || payload.filename,
      content_type: data.content_type || payload.content_type,
      parts: Array.isArray(data.parts) ? data.parts : [],
    }
  }

  processS3Upload(uploadId) {
    const tracked = this.uploads[uploadId]
    if (!tracked || tracked.strategy !== 's3') return
    if (tracked.processingPromise) return

    tracked.processingPromise = (async () => {
      try {
        await this.runS3UploadLoop(uploadId)
      } catch (error) {
        if (tracked.cancelRequested || tracked.pauseRequested) {
          return
        }
        console.error('S3 upload failed:', error)
        await this.abortS3Upload(tracked.session)
        this.store.failUpload(uploadId, error.message || 'Upload failed')
        if (tracked.options?.onError) tracked.options.onError(error)
        this.clearS3SessionMetadata(uploadId)
        delete this.uploads[uploadId]
      } finally {
        tracked.processingPromise = null
      }
    })()
  }

  async runS3UploadLoop(uploadId) {
    const tracked = this.uploads[uploadId]
    if (!tracked) return

    const { file, session } = tracked
    const configuredPartSize = this.getConfiguredPartSize(session)
    const totalParts = this.getPartCount(file.size, configuredPartSize)

    let uploadedBytes = this.calculateUploadedBytes(session, file.size)
    this.handleS3Progress(uploadId, uploadedBytes, file.size, tracked)

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (tracked.cancelRequested) return
      if (tracked.pauseRequested) {
        tracked.isPaused = true
        return
      }

      const existingPart = session.parts.find((part) => Number(part.part_number) === partNumber && part.etag)

      const partSize = this.getPartSize(partNumber, configuredPartSize, file.size)

      if (existingPart && existingPart.etag) {
        // Part already uploaded - skip it. uploadedBytes already includes it from calculateUploadedBytes
        continue
      }

      const partUploaded = await this.uploadSingleS3Part({
        uploadId,
        tracked,
        partNumber,
        startByte: (partNumber - 1) * configuredPartSize,
        partSize,
        uploadedBytes,
      })

      if (!partUploaded) {
        return
      }

      uploadedBytes += partSize
    }

    if (tracked.cancelRequested || tracked.pauseRequested) {
      return
    }

    await this.completeS3Upload(uploadId)
  }

  async uploadSingleS3Part({ uploadId, tracked, partNumber, startByte, partSize, uploadedBytes }) {
    if (tracked.cancelRequested || tracked.pauseRequested) {
      return false
    }

    const { file, session } = tracked
    const { signed_url, headers } = await this.requestS3SignedUrl(session, partNumber)
    const chunk = file.slice(startByte, startByte + partSize)

    const controller = new AbortController()
    tracked.currentAbortController = controller

    try {
      const response = await obsAxios.put(signed_url, chunk, {
        headers: {
          'Content-Type': session.content_type || DEFAULT_CONTENT_TYPE,
          ...(headers || {}),
        },
        signal: controller.signal,
        onUploadProgress: (event) => {
          this.handleS3Progress(uploadId, uploadedBytes + event.loaded, file.size, tracked)
        },
      })

      const etag = response?.headers?.etag || response?.headers?.ETag
      if (!etag) {
        throw new Error('OBS did not return an ETag for uploaded part')
      }

      const filteredParts = session.parts.filter((part) => Number(part.part_number) !== partNumber)
      filteredParts.push({
        part_number: partNumber,
        etag,
        size: chunk.size,
      })
      filteredParts.sort((a, b) => Number(a.part_number) - Number(b.part_number))
      session.parts = filteredParts

      this.persistS3SessionMetadata(uploadId, session)
      this.handleS3Progress(uploadId, uploadedBytes + chunk.size, file.size, tracked)
      return true
    } catch (error) {
      if (this.isAbortError(error)) {
        return false
      }
      throw error
    } finally {
      tracked.currentAbortController = null
    }
  }

  async requestS3SignedUrl(session, partNumber) {
    const payload = {
      file_type: session.file_type,
      object_key: session.object_key,
      upload_id: session.upload_id,
      part_number: partNumber,
      content_type: session.content_type,
    }

    const { data } = await axios.post(`${API_BASE_URL}/upload/s3/part-url/`, payload)
    return data
  }

  async completeS3Upload(uploadId) {
    const tracked = this.uploads[uploadId]
    if (!tracked) return
    const { session, file, options } = tracked

    const payload = {
      file_type: session.file_type,
      object_key: session.object_key,
      upload_id: session.upload_id,
      original_filename: session.original_filename || file.name,
      file_size: file.size,
      parts: session.parts
        .filter((part) => part.etag)
        .sort((a, b) => Number(a.part_number) - Number(b.part_number))
        .map((part) => ({
          part_number: Number(part.part_number),
          etag: part.etag,
        })),
    }

    // Include metadata (e.g., reference genome for BED files) in the payload
    if (options?.metadata) {
      Object.assign(payload, options.metadata)
    }

    // Most uploads complete on the shared route; one that names its own (the
    // somatic result files) completes there, and the path is kept in the upload's
    // metadata so a resumed upload completes on it too.
    const completePath = options?.metadata?.s3CompletePath || '/upload/s3/complete/'
    const { data } = await axios.post(`${API_BASE_URL}${completePath}`, payload)

    this.store.completeUpload(uploadId)
    this.clearS3SessionMetadata(uploadId)
    if (options.onSuccess) options.onSuccess(data)
    delete this.uploads[uploadId]
  }

  async abortS3Upload(session) {
    if (!session?.upload_id) return

    try {
      await axios.post(`${API_BASE_URL}/upload/s3/abort/`, {
        file_type: session.file_type,
        object_key: session.object_key,
        upload_id: session.upload_id,
      })
    } catch (error) {
      console.warn('Failed to abort OBS upload:', error)
    }
  }

  persistS3SessionMetadata(uploadId, session) {
    this.store.updateUploadMetadata(uploadId, (prev = {}) => ({
      ...prev,
      s3Session: cloneS3Session(session),
    }))
  }

  clearS3SessionMetadata(uploadId) {
    this.store.updateUploadMetadata(uploadId, (prev = {}) => {
      if (!prev.s3Session) return prev
      const { s3Session, ...rest } = prev
      return rest
    })
  }

  calculateUploadedBytes(session, totalSize) {
    if (!session?.parts?.length) return 0
    const partSize = this.getConfiguredPartSize(session)

    return session.parts.reduce((sum, part) => {
      if (!part?.etag) return sum
      if (part.size) return sum + part.size
      const normalizedPartNumber = Number(part.part_number)
      return sum + this.getPartSize(normalizedPartNumber, partSize, totalSize)
    }, 0)
  }

  getConfiguredPartSize(session) {
    if (!session?.part_size_bytes) {
      throw new Error('Multipart upload configuration is missing part_size_bytes')
    }
    return Number(session.part_size_bytes)
  }

  getPartSize(partNumber, configuredPartSize, totalSize) {
    const start = (partNumber - 1) * configuredPartSize
    const end = Math.min(start + configuredPartSize, totalSize)
    return Math.max(end - start, 0)
  }

  getPartCount(totalBytes, partSize) {
    if (!partSize) return 0
    return Math.max(1, Math.ceil(totalBytes / partSize))
  }

  handleS3Progress(uploadId, uploadedBytes, totalBytes, context) {
    const percentage = totalBytes === 0 ? '0.00' : ((uploadedBytes / totalBytes) * 100).toFixed(2)
    const elapsedTime = Date.now() - context.startTime
    const uploadedSinceStart = uploadedBytes - (context.startedBytesUploaded || 0)
    const uploadSpeed = elapsedTime > 0 ? uploadedSinceStart / (elapsedTime / 1000) : 0
    const remainingBytes = totalBytes - uploadedBytes

    let timeRemaining = 0
    if (uploadSpeed > 0) {
      timeRemaining = Math.max(0, Math.round(remainingBytes / uploadSpeed))
    }

    this.store.updateUploadProgress(uploadId, percentage, timeRemaining)
    if (context.options?.onProgress) {
      context.options.onProgress(percentage, timeRemaining)
    }
  }

  isAbortError(error) {
    return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.message === 'canceled'
  }
}

// Create singleton instance
const uploadService = new UploadService()

export default uploadService
